import type { TPlayer } from 'video.js'
import type { QualityLevel, QualityList } from '|>/core/index.h'

import { Events } from '|>/shared/events'
import { MAX_AUTO_QUALITY } from '|>/shared/types/quality'

export const autoQuality = { id: 'auto', width: 0, height: 0 }

export const getQualityItemPixels = (
  qualityItem: QualityLevel | Events.Media.QualityLevelSelected
) => {
  const { width, height } = qualityItem
  return width > height ? height : width
}

export const filterUniqueQualityLevels = (qualityLevels: QualityLevel[]) => {
  const seenPixels = new Set<number>()

  return qualityLevels.filter((current) => {
    const pixels = getQualityItemPixels(current)

    if (seenPixels.has(pixels)) {
      return false
    }

    seenPixels.add(pixels)
    return true
  })
}

export const sortQualityLevels = (qualityLevels: QualityLevel[]) => {
  const uniqueQualityLevels = filterUniqueQualityLevels(qualityLevels)

  return uniqueQualityLevels.sort((a, b) => {
    const { width: aWidth, height: aHeight } = a
    const { width: bWidth, height: bHeight } = b

    const aPixels = aWidth > aHeight ? aHeight : aWidth
    const bPixels = bWidth > bHeight ? bHeight : bWidth

    return bPixels - aPixels
  })
}

export const formatQualityLevels = (qualityLevels: QualityLevel[]) => {
  return sortQualityLevels(filterUniqueQualityLevels(qualityLevels))
}

export const QualityLabel: Record<string, string> = {
  '2160': '4K 2160p',
  '1440': '2K 1440p',
  '1080': 'FHD 1080p',
  '720': 'HD 720p',
  '480': 'SD 480p',
  '360': 'SD 360p',
  '240': 'SD 240p',
  '144': 'SD 144p',
}

interface GetQualityLabelOptions {
  quality?: QualityLevel | Events.Media.QualityLevelSelected
  isAuto?: boolean
}
export const getQualityLabel = ({
  quality,
  isAuto,
}: GetQualityLabelOptions) => {
  if (!quality) return ''

  const pixels = getQualityItemPixels(quality)

  return isAuto ? `${pixels}p` : (QualityLabel[pixels] ?? `${pixels}p`)
}

export const getQualityDimension = (
  quality?: QualityLevel | Events.Media.QualityLevelSelected
) => {
  if (!quality) return

  const { width, height } = quality

  if (width > height) return { dimension: 'height', value: height }

  return { dimension: 'width', value: width }
}

export const setQualityByDimensions = (
  player?: TPlayer,
  targetQuality?: QualityLevel | Events.Media.QualityLevelSelected
) => {
  if (!player) return

  const { dimension: targetDimension, value: targetValue } =
    getQualityDimension(targetQuality) ?? {}

  if (
    !targetQuality ||
    targetQuality?.id === 'auto' ||
    !targetDimension ||
    !targetValue
  )
    return

  const qualityList = player.qualityLevels()

  if (!qualityList || !qualityList.levels_) return

  let selectedIndex = qualityList.selectedIndex_
  let found = false
  for (let i = 0; i < qualityList.length; ++i) {
    const level = qualityList[i]
    const isMatch = level[targetDimension] === targetValue
    level.enabled = isMatch
    if (isMatch && !found) {
      selectedIndex = i
      found = true
    }
  }

  if (selectedIndex === -1) return
  qualityList.selectedIndex_ = selectedIndex

  const selectedQuality = qualityList.levels_[selectedIndex]
  if (selectedQuality)
    player.trigger(Events.Media.QualityLevelSelected, selectedQuality)
}

export const isAutoQualityPixelsEnbled = ({
  pixels,
  maxAutoQuality,
  isSelectedQualityAllowed,
}: {
  pixels: number
  maxAutoQuality?: MAX_AUTO_QUALITY
  isSelectedQualityAllowed?: Events.Player.IsSelectedQualityAllowed
}) => {
  if (
    isSelectedQualityAllowed &&
    !isSelectedQualityAllowed({ quality: pixels })
  ) {
    return false
  }

  if (
    maxAutoQuality === MAX_AUTO_QUALITY.AUTO ||
    maxAutoQuality === MAX_AUTO_QUALITY.HIGH
  )
    return true

  if (maxAutoQuality === MAX_AUTO_QUALITY.LOW) return pixels <= 360
  if (maxAutoQuality === MAX_AUTO_QUALITY.MEDIUM) return pixels <= 720
  return true
}

export const isAutoQuality = (qualityList: QualityList) => {
  if (!qualityList) return false

  return Array.from(qualityList.levels_).every((level) => level.enabled)
}
